// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import { isBlank, isExternalMode } from './confirmUpdateFormModel';

// Bulk-apply target scope, shared with the toolbar controls that set it so the
// producer and consumers reference one definition rather than bare strings.
export const SCOPE = {
  all: 'all',
  onlyUnset: 'onlyUnset',
};

/**
 * Mass-apply consent settings across an RHF values map keyed by pipelineId.
 * Returns a NEW values object; the input is never mutated, so a `reset(next)`
 * cleanly rebaselines the form.
 *
 * `scope` selects the target pipelines:
 *   - 'all'       → every pipeline.
 *   - 'onlyUnset' → only pipelines that are genuinely missing a category they
 *                   should have: non-external mode AND a blank consentCategoryId.
 *                   (External pipelines and ones that already have a category are
 *                   left untouched.)
 *
 * Per target, the order matters because the category rule is mode-aware:
 *   1. apply `mode` when provided (otherwise the mode is left as-is),
 *   2. read the resulting effective mode,
 *   3. external forbids a category → clear it (this overrides any categoryId),
 *      otherwise apply `categoryId` when provided.
 */
export function applyBulkConsent(values, { mode, categoryId, scope }) {
  const isTarget = pipelineValues => {
    if (scope === SCOPE.all) {
      return true;
    }
    return !isExternalMode(pipelineValues.consentMode) && isBlank(pipelineValues.consentCategoryId);
  };

  return Object.keys(values).reduce((next, pipelineId) => {
    const current = values[pipelineId];

    if (!isTarget(current)) {
      next[pipelineId] = current;
      return next;
    }

    const updated = { ...current };
    if (mode !== undefined) {
      updated.consentMode = mode;
    }

    if (isExternalMode(updated.consentMode)) {
      updated.consentCategoryId = '';
    } else if (categoryId !== undefined) {
      updated.consentCategoryId = categoryId;
    }

    next[pipelineId] = updated;
    return next;
  }, {});
}

/**
 * Mass-apply a single pipeline-variable value across an RHF values map keyed by
 * pipelineId. Returns a NEW values object; the input is never mutated.
 *
 * `scope` selects the target pipelines:
 *   - 'all'       → every pipeline.
 *   - 'onlyUnset' → only pipelines whose current value for `name` is blank
 *                   (a missing key counts as blank).
 */
export function applyBulkVariable(values, { name, value, scope }) {
  const isTarget = pipelineValues => scope === SCOPE.all || isBlank(pipelineValues[name]);

  return Object.keys(values).reduce((next, pipelineId) => {
    const current = values[pipelineId];
    next[pipelineId] = isTarget(current) ? { ...current, [name]: value } : current;
    return next;
  }, {});
}
